import { useId } from 'react';
import { Plus } from 'lucide-react';
import { PermissionGuard } from '@/features/auth';
import {
  ActiveFilterChips,
  ButtonLink,
  CardHeader,
  CardTitle,
  FilterPopover,
  Label,
  SearchBar,
  Select,
} from '@/shared/components';
import type { DocumentType, TemplateStatusFilter } from '../../types/documentTemplates.types';
import type { useTemplateListFilters } from '../../hooks/useTemplateListFilters';
import { useTemplateFilterChips } from '../../hooks/useTemplateFilterChips';
import { STATUS_FILTER_OPTIONS, TYPE_FILTER_OPTIONS } from '../../utils/templateListOptions';
import { TemplateOwnerFilter } from '../TemplateOwnerFilter';

interface Props {
  titleId: string;
  total: number | undefined;
  list: ReturnType<typeof useTemplateListFilters>;
}

const templateCount = (total: number) => `${total} ${total === 1 ? 'template' : 'templates'}`;

export function TemplateListToolbar({ titleId, total, list }: Props) {
  const { filters, setFilter } = list;
  const chips = useTemplateFilterChips(list);
  const typeLabelId = useId();
  const statusLabelId = useId();

  return (
    <CardHeader className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto space-y-0.5">
          <CardTitle id={titleId}>All templates</CardTitle>
          {total !== undefined && <p className="text-sm text-mp-muted">{templateCount(total)}</p>}
        </div>
        <SearchBar
          searchTerm={list.search}
          setSearchTerm={list.setSearch}
          placeholder="Search by name or key"
          className="xl:w-80"
        />
        <FilterPopover activeCount={list.activeCount} onClearAll={list.clearFilters}>
          <div>
            <Label id={typeLabelId}>Document type</Label>
            <Select
              options={TYPE_FILTER_OPTIONS}
              ariaLabelledBy={typeLabelId}
              value={filters.documentType ?? ''}
              onChange={(value) => setFilter('documentType', (value || undefined) as DocumentType | undefined)}
            />
          </div>
          <div>
            <Label id={statusLabelId}>Status</Label>
            <Select
              options={STATUS_FILTER_OPTIONS}
              ariaLabelledBy={statusLabelId}
              value={filters.status ?? ''}
              onChange={(value) => setFilter('status', (value || undefined) as TemplateStatusFilter | undefined)}
            />
          </div>
          <TemplateOwnerFilter
            owner={filters.owner}
            dealerId={filters.dealerId}
            onOwnerChange={list.setOwner}
            onDealerChange={(id) => setFilter('dealerId', id)}
          />
        </FilterPopover>
        <PermissionGuard permission={{ resource: 'document_template', action: 'add' }}>
          <ButtonLink
            to="/admin/document-templates/new"
            size="sm"
            startIcon={<Plus aria-hidden="true" className="size-4" />}
          >
            New template
          </ButtonLink>
        </PermissionGuard>
      </div>
      <ActiveFilterChips filters={chips} onClearAll={list.clearFilters} />
    </CardHeader>
  );
}
